<template>
  <div id="app" class="font-sans">
    <div class="container m-auto px-8 pb-16 md:px-16">
      <header class="site-masthead mb-6 md:mb-8 py-3">
        <h1 class="masthead-lockup">
          <span class="masthead-node" aria-hidden="true">
            <span class="masthead-node-frame"></span>
            <img
              src="https://i.ibb.co/SmBj1zJ/image-2-1.png"
              class="masthead-head"
              alt=""
            >
            <span class="masthead-corner"></span>
          </span>
          <svg
            class="masthead-connector"
            viewBox="0 0 44 34"
            aria-hidden="true"
          >
            <path d="M0 25H13L27 11H44"></path>
            <rect x="40" y="7" width="4" height="4"></rect>
          </svg>
          <span class="masthead-wordmark">
            <span class="masthead-kicker">Delmon Young</span>
            <span class="masthead-title">Trade Tree</span>
          </span>
        </h1>
      </header>
      <div class="md:flex items-start justify-between font-sans m-auto">
        <delmon-trade-tree
          :chart-data="chartData"
          :player-trades="playerTradeSnapshot"
          :player-war="playerWarSnapshot"
          class="w-full max-w-6xl"
        />
        <div class="mt-16 md:mt-0 ml-0 md:ml-16 w-full md:w-64 md:flex-shrink-0 overflow-x-auto border border-solid border-gray-400 bg-white">
          <table class="w-full text-sm">
            <thead>
              <tr class="uppercase text-gray-500 border-b-2 border-gray-700">
                <th scope="col" class="p-2 text-left text-xs font-normal align-bottom border-r border-gray-300">Year</th>
                <th scope="col" class="p-2 text-right text-xs font-normal align-bottom border-r border-gray-300">Delmon</th>
                <th scope="col" class="p-2 text-right text-xs font-normal align-bottom">Rays</th>
              </tr>
            </thead>
            <tbody class="font-mono">
              <tr
                v-for="year in yearlyData"
                :key="year.year"
                class="text-gray-900 border-b border-gray-200 hover:bg-gray-100"
              >
                <td class="px-2 py-1 font-normal text-left whitespace-no-wrap border-r border-gray-200">{{ year.year }}</td>
                <td class="p-0 text-right font-medium border-r border-gray-200"><table-cell :value="year.delmon" /></td>
                <td class="p-0 text-right font-medium"><table-cell :value="year.rays" /></td>
              </tr>
            </tbody>
            <tfoot class="font-mono">
              <tr class="border-t-2 border-gray-700 text-gray-900 font-medium">
                <th scope="row" class="px-2 py-2 font-sans font-normal text-gray-600 text-left whitespace-no-wrap">
                  Total WAR
                </th>
                <td class="px-2 py-2 text-right border-l border-r border-gray-300">
                  +{{ totalDelmonWAR }}
                </td>
                <td class="px-2 py-2 text-right">
                  +{{ totalRaysWAR }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <div class="mt-16 md:flex items-center justify-between text-sm text-gray-600">
        <p class="text-gray-600">
          Sources:
          <a href="https://www.baseball-reference.com" class="text-blue-600 font-medium" target="_blank" rel="noopener noreferrer">Baseball Reference</a>
          and
          <a href="https://www.mlb.com" class="text-blue-600 font-medium" target="_blank" rel="noopener noreferrer">MLB</a>.
          Local snapshots updated {{ snapshotUpdated }}.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';

import DelmonTradeTree from './components/DelmonTradeTree';
import TableCell from './components/TableCell';
import playerTradeSnapshot from './data/player-trades.json';
import playerWarSnapshot from './data/player-war.json';

const raysWarByYear = Object.values(playerWarSnapshot.players)
  .reduce((yearlyWar, player) => {
    player.seasons.forEach((season) => {
      yearlyWar[season.year] = (yearlyWar[season.year] || 0) + season.war;
    });

    return yearlyWar;
  }, {});

const signedWar = value => `${value >= 0 ? '+' : ''}${value.toFixed(1)}`;

export default {
  name: 'app',
  components: {
    DelmonTradeTree,
    TableCell,
  },

  data() {
    return {
      playerTradeSnapshot,
      playerWarSnapshot,
      yearlyData: [
        {
          year: 2008,
          delmon: '+0.3',
          rays: '+5.1',
        },
        {
          year: 2009,
          delmon: '-0.6',
          rays: '+9.8',
        },
        {
          year: 2010,
          delmon: '+1.9',
          rays: '+4.1',
        },
        {
          year: 2011,
          delmon: '-0.2',
          rays: '+2.8',
        },
        {
          year: 2012,
          delmon: '-0.9',
          rays: '+1.1',
        },
        {
          year: 2013,
          delmon: '-0.5',
          rays: '+1.3',
        },
        {
          year: 2014,
          delmon: '+1.0',
          rays: '+4.9',
        },
        {
          year: 2015,
          delmon: '+0.4',
          rays: '+6.0',
        },
        {
          year: 2016,
          delmon: '-',
          rays: '+2.9',
        },
        {
          year: 2017,
          delmon: '-',
          rays: '+1.7',
        },
        {
          year: 2018,
          delmon: '-',
          rays: '+0.7',
        },
        {
          year: 2019,
          delmon: '-',
          rays: '+6.5',
        },
        {
          year: 2020,
          delmon: '-',
          rays: '+1.1',
        },
        {
          year: 2021,
          delmon: '-',
          rays: '+4.7',
        },
        {
          year: 2022,
          delmon: '-',
          rays: '+2.9',
        },
        {
          year: 2023,
          delmon: '-',
          rays: '+6.3',
        },
        {
          year: 2024,
          delmon: '-',
          rays: '+6.8',
        },
        {
          year: 2025,
          delmon: '-',
          rays: '+3.3',
        },
        {
          year: 2026,
          delmon: '-',
          rays: '+0.0',
        },
      ],
      chartData: {
        name: 'Delmon Young',
        // img: 'https://i.ibb.co/qrqB9Q4/image-4.png',
        img: 'https://i.ibb.co/zG2xLHP/image-1.png',
        war: 2.1,
        children: [
          {
            name: 'Jason Bartlett',
            war: 10.4,
            service: '2008-2010',
            children: [
              {
                name: 'Adam Russell',
                war: 0.2,
                service: '2011',
              },
              {
                name: 'Cole Figueroa',
                war: -0.1,
                service: '2014',
              },
              {
                name: 'Brandon Gomes',
                war: -0.1,
                service: '2011-2015',
              },
              {
                name: 'Cesar Ramos',
                war: 1.4,
                service: '2011-2014',
              },
            ],
          },
          {
            name: 'Matt Garza',
            war: 8.5,
            service: '2008-2010',
            children: [
              {
                name: 'Hak-ju Lee',
                war: 0,
                service: 'Minors, 2011-2015',
              },
              {
                name: 'Brandon Guyer',
                war: 4.7,
                service: '2011-2016',
              },
              {
                name: 'Chris Archer',
                war: 12.4,
                service: '2012-2018, 2021',
                children: [
                  {
                    name: 'Tyler Glasnow',
                    war: 8.7,
                    service: '2018-2023',
                    children: [
                      {
                        name: 'Ryan Pepiot',
                        service: '2024-present',
                        active: true,
                        war: 4.3,
                      },
                      {
                        name: 'Jonny DeLuca',
                        service: '2024-present',
                        active: true,
                        war: 1.9,
                      },
                    ],
                  },
                  {
                    name: 'Austin Meadows',
                    war: 6.2,
                    service: '2018-2021',
                    children: [
                      {
                        name: 'Isaac Paredes',
                        war: 9.0,
                        service: '2022-2024',
                        children: [
                          {
                            name: 'Christopher Morel',
                            war: -1.1,
                            service: '2024-2025',
                          },
                          {
                            name: 'Hunter Bigge',
                            war: -0.2,
                            active: true,
                            service: '2024-present',
                          },
                          {
                            name: 'Ty Johnson',
                            war: 0,
                            active: true,
                            service: 'Minors, 2024-present',
                          },
                        ],
                      },
                      {
                        name: 'Ryan Cermak (via draft pick)',
                        war: 0,
                        active: true,
                        service: 'Minors, 2023-present',
                      },
                    ],
                  },
                  {
                    name: 'Shane Baz',
                    war: 3.3,
                    service: '2021-2025',
                    children: [
                      {
                        name: 'Slater de Brun',
                        war: 0,
                        active: true,
                        service: 'Minors, 2026-present',
                      },
                      {
                        name: 'Michael Forret',
                        war: 0,
                        service: 'Minors, 2026',
                        children: [
                          {
                            name: 'Tyler Wells',
                            war: 0,
                            active: true,
                            service: '2026-present',
                          },
                        ],
                      },
                      {
                        name: 'Caden Bodine',
                        war: 0,
                        active: true,
                        service: 'Minors, 2026-present',
                      },
                      {
                        name: 'Austin Overn',
                        war: 0,
                        active: true,
                        service: 'Minors, 2026-present',
                      },
                      {
                        name: 'Taj Marchand',
                        war: 0,
                        active: true,
                        service: 'Minors, 2026-present',
                      },
                    ],
                  },
                ],
              },
              {
                name: 'Sam Fuld',
                war: 2.3,
                service: '2011-2013',
              },
              {
                name: 'Robinson Chirinos',
                war: 0.2,
                service: '2011',
              },
            ],
          },
        ],
      },
    };
  },

  created() {
    this.yearlyData.forEach((year) => {
      year.rays = signedWar(raysWarByYear[year.year] || 0);
    });
  },

  computed: {
    snapshotUpdated() {
      const snapshots = [
        this.playerTradeSnapshot.meta.generatedAt,
        this.playerWarSnapshot.meta.generatedAt,
      ];
      const latestSnapshot = snapshots
        .map(value => new Date(value))
        .reduce((latest, snapshot) => snapshot > latest ? snapshot : latest);

      return new Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
        year: 'numeric',
      }).format(latestSnapshot);
    },

    totalRaysWAR() {
      return _.sum(_.map(this.yearlyData, year =>parseFloat(year.rays))).toFixed(1);
    },

    totalDelmonWAR() {
      return _.sum(_.map(this.yearlyData, year =>  isNaN(year.delmon) ? parseFloat(0) : parseFloat(year.delmon))).toFixed(1);
    },
  },
}
</script>

<style>
  .site-masthead {
    display: flex;
    justify-content: center;
  }

  .masthead-lockup {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #23313D;
  }

  .masthead-node {
    position: relative;
    width: 64px;
    height: 64px;
    flex: 0 0 64px;
  }

  .masthead-node-frame {
    position: absolute;
    inset: 5px;
    border: 1.5px solid #263746;
    background-color: #F8F7F2;
    background-image:
      linear-gradient(rgba(183, 193, 203, 0.24) 1px, transparent 1px),
      linear-gradient(90deg, rgba(183, 193, 203, 0.24) 1px, transparent 1px);
    background-size: 12px 12px;
  }

  .masthead-head {
    position: absolute;
    left: -2px;
    bottom: -2px;
    width: 68px;
    height: 68px;
    object-fit: contain;
    filter: drop-shadow(1px 2px 0 rgba(38, 55, 70, 0.12));
  }

  .masthead-corner {
    position: absolute;
    top: 1px;
    left: 1px;
    width: 10px;
    height: 10px;
    border-top: 2px solid #4D93BF;
    border-left: 2px solid #4D93BF;
  }

  .masthead-connector {
    width: 44px;
    height: 34px;
    margin: 0 10px 0 -5px;
    flex: 0 0 44px;
    overflow: visible;
    fill: #4D93BF;
  }

  .masthead-connector path {
    fill: none;
    stroke: #B7C1CB;
    stroke-width: 1.25;
    stroke-linecap: square;
    stroke-linejoin: miter;
    vector-effect: non-scaling-stroke;
  }

  .masthead-wordmark {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  .masthead-kicker {
    margin-bottom: 5px;
    color: #6B7782;
    font-family: 'Roboto Mono';
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.215em;
    line-height: 1;
    text-transform: uppercase;
  }

  .masthead-title {
    font-family: 'Roboto';
    font-size: 22px;
    font-weight: 500;
    letter-spacing: -0.035em;
    line-height: 1;
  }

  @media (max-width: 479px) {
    .masthead-node {
      width: 58px;
      height: 58px;
      flex-basis: 58px;
    }

    .masthead-head {
      width: 62px;
      height: 62px;
    }

    .masthead-connector {
      width: 34px;
      margin-right: 8px;
      flex-basis: 34px;
    }

    .masthead-kicker {
      font-size: 9px;
      letter-spacing: 0.18em;
    }

    .masthead-title {
      font-size: 19px;
    }
  }
</style>
